<template>
  <div ref="container" class="chart-graph">
    <canvas ref="chart"></canvas>
  </div>
</template>

<script setup lang="ts">
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
} from 'chart.js';
import { ref, watch } from "vue";
import { useIntersectionObserver } from '@vueuse/core'
import { ChartDataPoint, ChartTimePeriod, PriceChartCurrency } from "@kano/types/crypto-price-chart";
import { formatNumberStandard } from "@kano/helpers/numberFormatter";
import { i18n } from "@kano/i18n";

const props = defineProps<{
  fiatCurrency: PriceChartCurrency,
  datePeriod: ChartTimePeriod,
  chartData: Array<ChartDataPoint>,
}>();

Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
);

const container = ref<HTMLDivElement|null>(null);
const chart = ref<HTMLCanvasElement|null>(null);

let chartObject = null;

const { stop } = useIntersectionObserver(
  container,
  ([{ isIntersecting }]) => {
    if (isIntersecting && chart.value) {
      chartObject = createChart(props.chartData, chart.value);
      stop();
    }
  },
  { threshold: 0.5 },
);

watch(() => props.chartData, (value) => {
  if (!value.length || !chart.value || !chartObject) {
    return;
  }

  chartObject.data.datasets[0].data = value;
  chartObject.update();
});

const createChart = (chartData, chartElement) => {
  return new Chart(chartElement, {
    type: 'line',
    data: {
      labels: false,
      datasets: [{
        data: chartData,
        pointStyle: false,
        borderColor: '#5F70DB',
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.1,
      }],
    },
    options: {
      interaction: {
        intersect: false,
        mode: 'index',
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: true,
          usePointStyle: true,
          axis: 'x',
          callbacks: {
            title: (tooltipItemList) => chartMethods.formatStringToLongDate(tooltipItemList[0].label),
            beforeBody: (tooltipItemList) => chartMethods.formatAmountString(tooltipItemList[0].parsed.y),
            label: () => '',
          },
          titleFont: {
            size: 14,
          },
          bodyFont: {
            size: 20,
          },
        }
      },
      parsing: {
        xAxisKey: 'date',
        yAxisKey: 'price',
      },
      maintainAspectRatio: false,
      legend: {
        display: false,
      },
      scales: {
        y: {
          border: {
            display: false,
            dash: [6, 4]
          },
          grid: {
            display: true,
            drawTicks: false,
            z: 1,
          },
          ticks: {
            align: 'end',
            padding: 10,
            maxTicksLimit: 5,
            callback: function (val) {
              return chartMethods.formatNumberToShort(Number(val));
            }
          },
        },
        x: {
          border: {
            display: false,
          },
          grid: {
            display: false,
          },
          ticks: {
            align: 'start',
            padding: 10,
            maxTicksLimit: 7,
            callback: function (val) {
              const label = this.getLabelForValue(val);
              return chartMethods.formatStringToShortDate(label);
            },
          },
        },
      },
      resizeDelay: 100,
    },
  });
}

const chartMethods = {
  formatAmountString (amountString: string) {
    return formatNumberStandard(Number(amountString), i18n.locale, props.fiatCurrency.code);
  },
  formatStringToLongDate (dateString: string) {
    const date = new Date(dateString);

    if ([ChartTimePeriod.HOUR, ChartTimePeriod.DAY, ChartTimePeriod.WEEK, ChartTimePeriod.MONTH].includes(props.datePeriod)) {
      return date.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: 'numeric',
        month: 'short',
        year: 'numeric',
        day: 'numeric',
      });
    }

    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  },
  formatStringToShortDate (dateString: string) {
    const date = new Date(dateString);

    if ([ChartTimePeriod.HOUR, ChartTimePeriod.DAY].includes(props.datePeriod)) {
      return date.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: 'numeric',
      });
    }
    if ([ChartTimePeriod.WEEK, ChartTimePeriod.MONTH, ChartTimePeriod.THREE_MONTHS, ChartTimePeriod.SIX_MONTHS].includes(props.datePeriod)) {
      return date.toLocaleDateString(undefined, {
        month: 'numeric',
        day: 'numeric',
      });
    }

    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'numeric',
    });
  },
  formatNumberToShort (number: number) {
    return new Intl.NumberFormat('en', {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(number);
  }
};

</script>

<style scoped lang="scss">
.chart-graph {
  height: 323px;
  user-select: none;
}
</style>
